import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-grey.svg'
import Field from '../../../components/Form/Field'
import ButtonIcon from '../../../features/components/Buttons/ButtonIcon'
import ActionText from '../../../components/ActionText'

function MultipleOptionsField({
  listFieldName,
  fieldLabel,
  initialValue,
  fieldMaxLength,
  maxLengthSchema,
  isTextarea,
  formik,
  itemPath,
  minListQuantity = 1,
  maxListQuantity = 5,
  ...restFieldProps
}) {
  const { values, setFieldValue } = formik

  const fieldsList = values[listFieldName]

  const addAnotherFieldItemHandler = useCallback(
    (initialValue = '') => {
      setFieldValue(listFieldName, [...fieldsList, { text: initialValue, id: uuidv4() }])
    },
    [setFieldValue, fieldsList, listFieldName]
  )

  const showAddAnotherButton = useMemo(() => {
    if (!maxListQuantity && maxListQuantity !== 0) {
      // if maxListQuantity is not set, allow adding fields
      return true
    } else {
      return fieldsList?.length < maxListQuantity
    }
  }, [maxListQuantity, fieldsList])

  const removeFieldItem = useCallback(
    deleteIndex => {
      if (fieldsList.length > 1) {
        const filteredList = fieldsList.filter((item, index) => {
          return index !== deleteIndex
        })
        setFieldValue(listFieldName, filteredList)
      }
    },
    [setFieldValue, fieldsList, listFieldName]
  )

  const getName = useCallback(({ itemPath, listFieldName, index }) => {
    return !!itemPath ? `${itemPath}.${listFieldName}[${index}].text` : `${listFieldName}[${index}].text`
  }, [])

  const getMaxLength = useCallback(({ maxLengthSchema, fieldMaxLength, index }) => {
    if (maxLengthSchema) {
      return maxLengthSchema[index] || maxLengthSchema.default
    } else if (fieldMaxLength) {
      return fieldMaxLength
    } else {
      return null
    }
  }, [])

  return (
    <>
      {fieldsList?.map((item, index) => (
        <Field
          key={item.id || index}
          placeholder={`${fieldLabel} ${index + 1}`}
          formik={formik}
          name={getName({ itemPath, listFieldName, index })}
          maxLength={getMaxLength({ maxLengthSchema, fieldMaxLength, index })}
          symbol={
            fieldsList?.length > minListQuantity ? (
              <ButtonIcon onClick={() => removeFieldItem(index)} Icon={CloseIcon} />
            ) : null
          }
          showSymbol
          isTextarea={isTextarea}
          symbolPosition={'end'}
          enableReinitialize
          {...restFieldProps}
        />
      ))}
      {showAddAnotherButton && (
        <ActionText onClick={() => addAnotherFieldItemHandler(initialValue)}>Add another</ActionText>
      )}
    </>
  )
}

MultipleOptionsField.propTypes = {
  listFieldName: PropTypes.string,
  fieldLabel: PropTypes.string,
  initialValue: PropTypes.string,
  fieldMaxLength: PropTypes.number,
  maxLengthSchema: PropTypes.shape({
    default: PropTypes.number.isRequired
  }),
  formik: PropTypes.object,
  itemPath: PropTypes.string,
  // control how many fields could be created/deleted
  minListQuantity: PropTypes.number,
  maxListQuantity: PropTypes.number
}

export default MultipleOptionsField
